import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getCoordinates, getCoordinatesKey } from '../../../api-client/identity'
import { APIErrors } from '../../../api-client/enums'
import isValidUSZip from '../../../utils/is-invalid-us-zip'
import { FormField } from '../../inputs'
import { Button } from '../../button'
import Modal from '../../modal'
import Loader from '../../loader'

export default function ZipCodeModal({ onLocationResolve, onClose }) {
  const [zipSubmitted, setZipSubmitted] = useState(false)
  const [zipCode, setZipCode] = useState('')
  const [error, setError] = useState(null)

  const { isFetching, remove, refetch: fetchCoords } = useQuery({
    queryKey: getCoordinatesKey,
    queryFn: () => getCoordinates({ zipCode }),
    enabled: false,
    onSuccess: (data) => {
      setError(null)
      onLocationResolve({ latitude: data.lat, longitude: data.lng, zipCode: zipCode })
    },
    onError: (queryError) => {
      setError(queryError)
    },
  })

  const handleChange = (value) => {
    if (zipSubmitted) setZipSubmitted(false)
    if (error) remove()
    setZipCode(value)
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (isValidUSZip(zipCode)) {
      fetchCoords()
    }

    setZipSubmitted(true)
  }

  const isInvalidZip =
    error?.errors &&
    (+error.errors[0]?.code === APIErrors.InvalidGoogleApiResponseStatusCode ||
      +error.errors[0]?.code === APIErrors.InvalidZipCode ||
      +error.errors[0]?.code === APIErrors.ZipCodeDoesNotExists ||
      +error.errors[0]?.code === APIErrors.TimezoneNotFound ||
      +error.errors[0]?.code === APIErrors.TimezoneNotSupported)

  return (
    <Modal onClose={onClose}>
      <form className='wrapper flex flexDirectionColumn alignCenter' onSubmit={handleSubmit}>
        <h2 className='title'>Would you like to see vehicles near you?</h2>
        <label htmlFor='zip'>Enter your postal code.</label>
        <div className='formFieldItem'>
          <FormField
            disabled={isFetching}
            id='zip'
            error={(zipSubmitted && !isValidUSZip(zipCode)) || error}
            inputMode='numeric'
            value={zipCode}
            onChange={(e) => handleChange(e.target.value)}
            data-hj-allow
          />
          {(error || (zipSubmitted && !isValidUSZip(zipCode))) && (
            <p className='error'>
              {isInvalidZip || !isValidUSZip(zipCode)
                ? 'Postal code is invalid'
                : 'Something went wrong'}
            </p>
          )}
        </div>
        <Button disabled={isFetching} type='submit' className='submitBtn'>
          <span>Continue</span>
          {isFetching && <Loader width='20' height={'20'} delay={200} />}
        </Button>

        <style jsx>{`
          .wrapper {
            position: relative;
            width: 43.6rem;
            padding: 2.6rem 2rem 4.1rem;
            background: #fff;

            .title {
              text-align: center;
              font-size: 3.2rem;
              line-height: 3.6rem;
            }

            label {
              margin: 0.3rem 0 1.52rem;
              font-size: 1.6rem;
            }

            .formFieldItem {
              width: 100%;
            }

            :global(.submitBtn) {
              margin: 1.48rem 0 0;

              span {
                vertical-align: middle;
              }

              :global(.loaderSvg) {
                margin: 0 0 0 1rem;
                vertical-align: middle;
              }
            }

            .error {
              margin: 0.5rem 0 0;
              color: var(--alert-color);
              text-align: center;
              font-size: 1.2rem;
              font-weight: 700;
            }
          }

          @media (max-width: 1024px) {
            .wrapper {
              width: 100%;
              padding: 3rem;

              .title {
                max-width: 18rem;
                padding: 0 0 0.5rem;
                line-height: 2.3rem;
                font-size: 1.8rem;
              }

              label {
                margin: 0.7rem 0 1.2rem;
              }

              :global(.flatBtn) {
                position: fixed;
                bottom: 2.1rem;
                width: calc(100% - 2.8rem);
              }
            }
          }
        `}</style>
      </form>
    </Modal>
  )
}
